import React, { useState, useEffect } from 'react';
import { 
  BoltIcon, 
  SignalIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface Equipment {
  id: string;
  name: string;
  type: string;
  status: 'online' | 'offline' | 'warning';
  power: number;
  efficiency: number;
  lastUpdate: string;
}

const Monitoring: React.FC = () => {
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchEquipmentData();
    const interval = setInterval(fetchEquipmentData, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchEquipmentData = () => {
    // Mock real-time data
    const mockEquipment: Equipment[] = [
      {
        id: '1',
        name: 'Solar Array A1',
        type: 'Solar Panel',
        status: 'online',
        power: Math.random() * 1000 + 500,
        efficiency: Math.random() * 20 + 80,
        lastUpdate: new Date().toISOString()
      },
      {
        id: '2',
        name: 'Inverter Unit 1',
        type: 'Inverter',
        status: 'online',
        power: Math.random() * 800 + 400,
        efficiency: Math.random() * 15 + 85,
        lastUpdate: new Date().toISOString()
      },
      {
        id: '3',
        name: 'Battery Bank B1',
        type: 'Battery',
        status: 'warning',
        power: Math.random() * 600 + 300,
        efficiency: Math.random() * 10 + 75,
        lastUpdate: new Date().toISOString()
      },
      {
        id: '4',
        name: 'Wind Turbine 1',
        type: 'Wind',
        status: 'offline',
        power: 0,
        efficiency: 0,
        lastUpdate: new Date(Date.now() - 300000).toISOString()
      }
    ];
    
    setEquipment(mockEquipment);
    setLoading(false);
  };

  const filteredEquipment = equipment.filter(item => 
    filter === 'all' || item.status === filter
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-500';
      case 'warning': return 'text-yellow-500';
      case 'offline': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'online': return 'bg-green-100 dark:bg-green-900/30';
      case 'warning': return 'bg-yellow-100 dark:bg-yellow-900/30';
      case 'offline': return 'bg-red-100 dark:bg-red-900/30';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Real-time Monitoring</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Live equipment status and performance metrics</p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span>Live Data</span>
          </div>
          
          <button 
            onClick={fetchEquipmentData}
            className="bg-white dark:bg-gray-800 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-300"
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid-1-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {equipment.filter(e => e.status === 'online').length}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">units</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Online</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Operating Normally</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {equipment.filter(e => e.status === 'warning').length}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">units</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Warning</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Needs Attention</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center">
              <SignalIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {equipment.filter(e => e.status === 'offline').length}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">units</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Offline</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Not Responding</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <BoltIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {Math.round(equipment.reduce((sum, e) => sum + e.power, 0))}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">kW</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Total Power</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Current Output</div>
          </div>
        </div>
      </div>

      {/* Equipment List */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Equipment Status</h2>
            
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Equipment</option>
              <option value="online">Online Only</option>
              <option value="warning">Warnings</option>
              <option value="offline">Offline</option>
            </select>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {filteredEquipment.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(item.status)}`} />
                  
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-white">{item.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{item.type}</div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="font-semibold text-gray-900 dark:text-white">{Math.round(item.power)} kW</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Power</div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-semibold text-gray-900 dark:text-white">{Math.round(item.efficiency)}%</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Efficiency</div>
                  </div>
                  
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBg(item.status)} ${getStatusColor(item.status)}`}>
                    {item.status.toUpperCase()}
                  </div>
                  
                  <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                    <EyeIcon className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Monitoring;